import { defineComponent } from 'vue';
import useAnchor from '@dts/shared/src/hooks/useAnchor';
import { RouterView } from 'vue-router';
import { NMessageProvider, NConfigProvider, GlobalThemeOverrides, NDialogProvider } from 'naive-ui';
import {
  PRIMARY_COLOR,
  PRIMARY_COLOR_DISABLED,
  PRIMARY_COLOR_HOVER,
} from '@dts/shared/src/config/color';

const themeOverrides: GlobalThemeOverrides = {
  common: {
    primaryColor: PRIMARY_COLOR,
    primaryColorHover: PRIMARY_COLOR_HOVER,
    primaryColorPressed: '#1773FE',
    fontFamily: 'BMWTypeNext, serif',
    fontSize: '.16rem',
    fontSizeHuge: '.24rem',
    fontSizeLarge: '.2rem',
    fontSizeMedium: '.16rem',
    fontSizeSmall: '.14rem',
    fontSizeMini: '.12rem',
  },
  BackTop: {
    width: '.48rem',
    height: '.48rem',
    iconSize: '.24rem',
    borderRadius: '.24rem',
    boxShadow:
      '0 .40rem .16rem -0.24rem rgba(0,0,0,0.04), 0 0.8rem .16rem -0.8rem rgba(0,0,0,0.12), 0 .16rem .40rem .16rem rgba(0,0,0,0.04)',
    boxShadowHover:
      '0 .40rem .16rem -0.24rem rgba(0,0,0,0.04), 0 0.8rem .16rem -0.8rem rgba(0,0,0,0.12), 0 .16rem .40rem .16rem rgba(0,0,0,0.04)',
    boxShadowPressed:
      '0 .40rem .16rem -0.24rem rgba(0,0,0,0.04), 0 0.8rem .16rem -0.8rem rgba(0,0,0,0.12), 0 .16rem .40rem .16rem rgba(0,0,0,0.04)',
  },
  Breadcrumb: {
    fontSize: '.16rem',
    fontWeightActive: '500',
  },
  Button: {
    colorPrimary: PRIMARY_COLOR,
    colorDisabledPrimary: PRIMARY_COLOR_DISABLED,
    colorHoverPrimary: PRIMARY_COLOR_HOVER,
    colorPressedPrimary: '#08358C',
    colorFocusPrimary: PRIMARY_COLOR_HOVER,
    paddingLarge: '.12rem .24rem',
    paddingMedium: '.08rem .16rem',
    paddingSmall: '.04rem .12rem',
    paddingTiny: '.02rem .08rem',
    fontSizeLarge: '.2rem',
    fontSizeMedium: '.16rem',
    fontSizeSmall: '.14rem',
    fontSizeTiny: '.12rem',
    fontWeight: '400',
    fontWeightStrong: '700',
    borderRadiusTiny: '0',
    borderRadiusSmall: '0',
    borderRadiusMedium: '0',
    borderRadiusLarge: '0',
    textColorDisabled: '#C3C8D0',
    borderDisabled: '.01rem solid #C3C8D0',
    borderDisabledPrimary: '.01rem solid #B8D4FF',
    iconSizeHuge: '.24rem',
    iconSizeLarge: '.2rem',
    iconSizeMedium: '.18rem',
    iconSizeSmall: '.16rem',
    iconSizeTiny: '.14rem',
    // heightTiny: '.24rem',
    // heightSmall: '.32rem',
    // heightMedium: '.48rem',
    // heightLarge: '.80rem',
    paddingRoundTiny: '0 .12rem',
    paddingRoundSmall: '0 .24rem',
    paddingRoundMedium: '0 .48rem',
    paddingRoundLarge: '0 .80rem',
    opacityDisabled: '1',
    iconMarginSmall: '.08rem',
    iconMarginMedium: '.08rem',
    iconMarginLarge: '.12rem',
  },
  Checkbox: {
    sizeMedium: '.16rem',
    fontSizeMedium: '.16rem',
    borderRadius: '.04rem',
  },
  Card: {
    paddingSmall: '.20rem',
    paddingMedium: '.20rem',
    paddingLarge: '.20rem',
    paddingHuge: '.20rem',
  },
  Cascader: {
    menuHeight: '2.90rem',
    optionHeight: '.38rem',
  },
  DataTable: {
    fontSizeMedium: '.16rem',
    thColor: '#EBEDF0',
    thIconColor: '#ccc',
    tdColorHover: 'rgba(79, 178, 51, .1)',
    thButtonColorHover: '#0000',
    thColorModal: '#EBEDF0',
    thPaddingSmall: '.10rem .20rem',
    thPaddingMedium: '.10rem .20rem',
    thPaddingLarge: '.10rem .20rem',
    tdPaddingSmall: '.10rem .20rem',
    tdPaddingMedium: '.10rem .20rem',
    tdPaddingLarge: '.10rem .20rem',
  },
  DatePicker: {
    itemBorderRadius: '.14rem',
    arrowSize: '.24rem',
    itemFontSize: '.16rem',
    calendarDaysFontSize: '.16rem',
    calendarTitleFontSize: '.16rem',
    panelActionPadding: '.12rem .20rem',
    itemSize: '.28rem',
    itemCellHeight: '.34rem',
    itemCellWidth: '.40rem',
    calendarTitlePadding: '0 .08rem .08rem .08rem',
    calendarTitleHeight: '.32rem',
    calendarLeftPaddingDate: '.12rem .16rem .09rem .16rem',
    calendarLeftPaddingDaterange: '.12rem .16rem. .09rem .16rem',
    calendarLeftPaddingDatetime: '.06rem .16rem. .09rem .16rem',
    calendarLeftPaddingDatetimerange: '.06rem .16rem. .09rem .16rem',
    calendarRightPaddingDaterange: '.12rem .16rem. .09rem .16rem',
    calendarRightPaddingDatetimerange: '.06rem .16rem. .09rem .16rem',
    panelHeaderPadding: '.12rem .20rem. .08rem .20rem',
  },
  Dialog: {
    padding: '.40rem',
    iconSize: '.36rem',
    iconMarginIconTop: '0 0 .12rem 0',
    closeMarginIconTop: '.24rem .24rem 0 0',
    titleFontSize: '.24rem',
    fontSize: '.16rem',
    actionSpace: '.20rem',
    contentMargin: '.12rem 0 .40rem 0',
  },
  Dropdown: {
    padding: '.04rem 0',
    fontSizeHuge: '.24rem',
    fontSizeLarge: '.2rem',
    fontSizeMedium: '.16rem',
    fontSizeSmall: '.14rem',
    fontSizeMini: '.12rem',
    optionIconSizeHuge: '.18rem',
    optionIconSizeLarge: '.16rem',
    optionIconSizeMedium: '.16rem',
    optionIconSizeSmall: '.14rem',
    optionSuffixWidthSmall: '0.14rem',
    optionSuffixWidthMedium: '0.14rem',
    optionSuffixWidthLarge: '0.16rem',
    optionSuffixWidthHuge: '0.16rem',
    optionIconSuffixWidthSmall: '0.32rem',
    optionIconSuffixWidthMedium: '0.32rem',
    optionIconSuffixWidthLarge: '0.36rem',
    optionIconSuffixWidthHuge: '0.36rem',
    optionPrefixWidthSmall: '.14rem',
    optionPrefixWidthMedium: '.14rem',
    optionPrefixWidthLarge: '.16rem',
    optionPrefixWidthHuge: '.16rem',
    optionIconPrefixWidthSmall: '.36rem',
    optionIconPrefixWidthMedium: '.36rem',
    optionIconPrefixWidthLarge: '.40rem',
    optionIconPrefixWidthHuge: '.40rem',
    optionHeightSmall: '.28rem',
    optionHeightMedium: '.34rem',
    optionHeightLarge: '.40rem',
    optionHeightHuge: '.46rem',
    borderRadius: '.03rem',
  },
  DynamicTags: {
    peers: {
      Tag: {
        heightMedium: '.24rem',
      },
      Input: {
        heightSmall: '.24rem',
      },
    },
  },
  Input: {
    heightMedium: '.32rem',
    paddingTiny: '0 .8rem',
    paddingSmall: '0 .10rem',
    paddingMedium: '0 .12rem',
    paddingLarge: '0 .14rem',
    clearSize: '.16rem',
    heightTiny: '.22rem',
    heightSmall: '.28rem',
    heightLarge: '.40rem',
    fontSizeTiny: '.12rem',
    fontSizeSmall: '.14rem',
    fontSizeMedium: '.14rem',
    fontSizeLarge: '.15rem',
    iconSize: '.24rem',
  },
  // InternalSelection: {
  //   heightMedium: '.32rem',
  //   fontSizeMedium: '.16rem',
  //   paddingSingle: '0 .36rem 0 .12rem',
  // },
  // InternalSelectMenu: {
  //   height: '2.90rem',
  //   optionHeightMedium: '.32rem',
  //   paddingSmall: '.04rem 0',
  //   paddingMedium: '.06rem 0',
  //   paddingLarge: '.06rem 0',
  //   paddingHuge: '.08rem 0',
  // },
  Form: {
    blankHeightSmall: '.20rem',
    blankHeightMedium: '.20rem',
    blankHeightLarge: '.20rem',
    feedbackHeightSmall: '.20rem',
    feedbackHeightMedium: '.20rem',
    feedbackHeightLarge: '.20rem',

    labelHeightSmall: '.22rem',
    labelHeightMedium: '.22rem',
    labelHeightLarge: '.22rem',
    labelTextAlignHorizontal: 'left',
  },

  Message: {
    padding: '.12rem .20rem .12rem .12rem',
    maxWidth: '7.20rem',
    minWidth: '4.20rem',
    iconMargin: '0 .20rem 0 0',
    closeMargin: '0 -0.08rem 0 .20rem',
    closeSize: '.15rem',
    fontSize: '.14rem',
    iconSize: '.20rem',
  },
  Pagination: {
    itemSizeMedium: '.32rem',
    itemPaddingMedium: '0',
    itemFontSizeMedium: '.16rem',
    inputWidthMedium: '.80rem',
    selectWidthMedium: '1.00rem',
    inputMarginMedium: '0 .20rem',
    itemMarginMedium: '0 0 0 .20rem',
  },
  Popover: {
    fontSize: '.16rem',
    arrowOffset: '.40rem',
    arrowOffsetVertical: '.16rem',
    arrowHeight: '.08rem',
    space: '.04rem',
    spaceArrow: '.14rem',
    padding: '.12rem .20rem',
  },
  Progress: {
    fontWeightCircle: '700',
    railHeight: '.04rem',
    fontSizeCircle: '.24rem',
    iconSizeCircle: '.30rem',
    iconSizeLine: '.20rem',
  },
  Switch: {
    railHeightMedium: '.15rem',
    railHeightLarge: '.20rem',
    railBorderRadiusMedium: '.08rem',
    railBorderRadiusLarge: '.10rem',
    railWidthMedium: '.40rem',
    railWidthLarge: '.50rem',
    buttonHeightMedium: '.24rem',
    buttonHeightLarge: '.32rem',
    buttonWidthMedium: '.24rem',
    buttonWidthLarge: '.32rem',
    buttonWidthPressedMedium: '.30rem',
    buttonWidthPressedLarge: '.38rem',
    buttonBorderRadiusMedium: '.12rem',
    buttonBorderRadiusLarge: '.16rem',
    buttonBoxShadow: '0 .02rem .03rem 0 rgba(0,0,0,0.10)',
  },
  Table: {
    thColor: '#EBEDF0',
    thPaddingSmall: '.10rem .20rem',
    thPaddingMedium: '.10rem .20rem',
    thPaddingLarge: '.10rem .20rem',
    tdPaddingSmall: '.10rem .20rem',
    tdPaddingMedium: '.10rem .20rem',
    tdPaddingLarge: '.10rem .20rem',
  },
  Tag: {
    borderRadius: '1rem',
    color: 'rgba(153,153,153,0.10)',
    padding: '0 .12rem',
    closeMargin: '0 0 0 .08rem',
    heightMedium: '.24rem',
    closeSizeSmall: '.20rem',
    closeSizeMedium: '.20rem',
    closeSizeLarge: '.20rem',
    fontSizeSmall: '.12rem',
    fontSizeMedium: '.16rem',
    fontSizeLarge: '.16rem',
  },
  TimePicker: {
    itemFontSize: '.16rem',
    itemHeight: '.38rem',
    itemWidth: '.74rem',
    panelActionPadding: '.12rem 0',
  },
  Tooltip: {
    peers: {
      Popover: {
        padding: '.20rem',
        fontSize: '.16rem',
      },
    },
  },
};

export default defineComponent({
  setup() {
    useAnchor();
  },

  render() {
    return (
      <NConfigProvider themeOverrides={themeOverrides}>
        <NMessageProvider>
          <NDialogProvider>
            <RouterView />
          </NDialogProvider>
        </NMessageProvider>
      </NConfigProvider>
    );
  },
});
